<template>
	<dialog-component custom-class="limit-wrap" @close="onClose">
		<p class="limit-title">{{ title }}</p>
		<p v-if="description" class="limit-description">{{ description }}</p>

		<!-- Карточек нет, когда среза /usage не пришло: цифры взять неоткуда,
		     а «0 из 0» врёт сильнее, чем отсутствие блока. -->
		<div v-if="cards.length" :class="['limit-cards', { 'limit-cards--all': isAll }]">
			<div v-for="card in cards" :key="card.key" class="limit-card">
				<p class="limit-card-label">{{ card.label }}</p>
				<p class="limit-card-value">
					<span :class="['limit-card-number', { 'limit-card-number--empty': card.value === 0 }]">{{ card.value }}</span>
					{{ LIMIT_DIALOG_CARD_SUFFIX }}
				</p>
			</div>
		</div>

		<p v-if="isAll" class="limit-note">{{ LIMIT_DIALOG_NOTE }}</p>

		<div class="limit-btn-group">
			<base-button v-if="isAll" :text="LIMIT_DIALOG_PROFILE_CTA" @click="goToProfile" />
			<base-button v-else :text="LIMIT_DIALOG_CLOSE_CTA" variant="secondary" @click="onClose" />
		</div>
	</dialog-component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useUsageStore, type UsageResource } from '@/stores/usage'
import {
	LIMIT_DIALOG_CARD_LABELS,
	LIMIT_DIALOG_CARD_LABELS_SHORT,
	LIMIT_DIALOG_CARD_SUFFIX,
	LIMIT_DIALOG_CLOSE_CTA,
	LIMIT_DIALOG_DESCRIPTIONS,
	LIMIT_DIALOG_NOTE,
	LIMIT_DIALOG_PROFILE_CTA,
	LIMIT_DIALOG_TITLES,
	limitDialogAllDescription,
	type LimitDialogVariant
} from '@/constants/pro'

const props = withDefaults(defineProps<{ variant?: LimitDialogVariant }>(), { variant: 'text' })

const emit = defineEmits<{ close: [] }>()

const router = useRouter()
const usageStore = useUsageStore()

const RESOURCES: UsageResource[] = ['text', 'image', 'song']

const isAll = computed(() => props.variant === 'all')

const title = computed(() => LIMIT_DIALOG_TITLES[props.variant])

const description = computed(() => {
	const limits = usageStore.limits
	if (!limits) return ''
	if (isAll.value) return limitDialogAllDescription(limits.text.limit, limits.image.limit, limits.song.limit)
	const resource = props.variant as UsageResource
	return LIMIT_DIALOG_DESCRIPTIONS[resource](limits[resource].used, limits[resource].limit)
})

const cards = computed(() => {
	const limits = usageStore.limits
	if (!limits) return []
	const keys = isAll.value ? RESOURCES : RESOURCES.filter((key) => key !== props.variant)
	const labels = isAll.value ? LIMIT_DIALOG_CARD_LABELS_SHORT : LIMIT_DIALOG_CARD_LABELS
	return keys.map((key) => ({ key, label: labels[key], value: limits[key].remaining }))
})

function onClose() {
	emit('close')
}

function goToProfile() {
	onClose()
	router.push('/profile')
}
</script>

<style lang="scss" scoped>
.limit {
	&-title {
		font: var(--font-title);
		color: var(--text-primary);
		text-align: center;
		margin-bottom: 12px;
	}
	&-description {
		font: var(--font-body-small);
		color: var(--text-secondary);
		text-align: center;
		margin-bottom: 24px;
	}
	&-cards {
		display: flex;
		gap: 12px;
		width: 100%;
		margin-bottom: 24px;
		// Вариант «все лимиты»: карточек три вместо двух — теснее и по центру,
		// иначе подписи в средних колонках переносятся.
		&--all {
			gap: 8px;
			.limit-card {
				padding: 8px;
				text-align: center;
			}
			// Три нуля здесь — не ошибка, а состояние тарифа: красным не кричим.
			.limit-card-value,
			.limit-card-number {
				color: var(--text-brand);
			}
		}
	}
	&-card {
		// Карточки делят ряд поровну и при двух, и при трёх: basis 0 вместо auto,
		// иначе «Текстовые запросы» растянет свою колонку шире соседних.
		flex: 1 1 0;
		min-width: 0;
		padding: 16px;
		border-radius: var(--radius-md);
		background-color: var(--background-secondary);
		&-label {
			font: var(--font-body-small);
			color: var(--text-secondary);
			margin-bottom: 4px;
		}
		&-value {
			font: var(--font-heading);
			color: var(--text-primary);
		}
		&-number {
			color: var(--text-brand);
			&--empty {
				color: var(--status-error);
			}
		}
	}
	&-note {
		font: var(--font-caption-large);
		color: var(--text-primary);
		text-align: center;
		padding: 12px;
		border-radius: var(--radius-md);
		background-color: var(--background-secondary);
		width: 100%;
		margin-bottom: 24px;
	}
	&-btn-group {
		width: 100%;
		// перебиваем padding: 15px 20px из .btn в BaseButton
		.btn {
			width: 100%;
			padding: 14px 12px;
			min-height: 52px;
		}
	}
}
</style>
